import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/shared/components";
import { useGetCommercialOrganizationsQuery } from "../../api";
import { useTemplatesPage } from "./hooks/useTemplatesPage";
import { LegalTemplatesPageSkeleton } from "./components";
import TemplatesFilterPanel from "./components/TemplatesFilterPanel";

const TemplatesCardsView = lazy(() => import("./components/TemplatesCardsView").then((m) => ({ default: m.default })));

export default function LegalTemplatesPage() {
  const { t } = useTranslation("LegalTemplatesPage");
  const { data: commercialOrganizations = [], isLoading: isLoadingOrganizations } =
    useGetCommercialOrganizationsQuery();

  const {
    filteredTemplates,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    handleResetFilters,
    hasActiveFilters,
  } = useTemplatesPage();

  if (isLoadingOrganizations) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
          </div>
          <LegalTemplatesPageSkeleton />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
              <span className="text-body-regular-md content-base-secondary">
                {t("counter", { count: filteredTemplates.length })}
              </span>
            </div>

            {filteredTemplates.length > 0 ? (
              <Suspense fallback={<LegalTemplatesPageSkeleton variant="cardsOnly" />}>
                <TemplatesCardsView
                  templates={filteredTemplates}
                  commercialOrganizations={commercialOrganizations}
                />
              </Suspense>
            ) : (
              <EmptyState
                hasActiveFilters={hasActiveFilters}
                noResultsText={t("emptyFiltered")}
                noDataText={t("empty")}
                noDataHint={t("emptyHint")}
              />
            )}
          </div>

          <TemplatesFilterPanel
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            onReset={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </section>
    </>
  );
}
